import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';

export type MediaView = ResponseBody<Operation<'/admin/media', 'get'>>['items'][number];
export type MediaStatus = MediaView['status'];

export type MediaKind = RequestBody<Operation<'/admin/media/uploads', 'post'>>['kind'];
export type ImageKind = Extract<MediaKind, 'product_image' | 'brand_logo'>;

const IMAGE_TYPES_OF_KIND: Readonly<Record<ImageKind, readonly string[]>> = {
  product_image: ['image/jpeg', 'image/png', 'image/webp', 'image/avif'],
  brand_logo: ['image/jpeg', 'image/png', 'image/webp'],
};

export const acceptOf = (kind: ImageKind): string => IMAGE_TYPES_OF_KIND[kind].join(',');

const SETTLED: readonly MediaStatus[] = ['ready', 'rejected', 'expired'];

export const isAcceptedImage = (kind: ImageKind, file: Pick<File, 'type'>): boolean =>
  IMAGE_TYPES_OF_KIND[kind].includes(file.type);

export const isSettled = (status: MediaStatus): boolean => SETTLED.includes(status);

const HEX_RADIX = 16;
const BYTE_WIDTH = 2;

export const sha256Hex = async (file: Blob): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(HEX_RADIX).padStart(BYTE_WIDTH, '0'))
    .join('');
};

export const imageFiles = (
  kind: ImageKind,
  items: Iterable<File>,
): { readonly accepted: File[]; readonly refused: File[] } => {
  const files = [...items];
  return {
    accepted: files.filter((file) => isAcceptedImage(kind, file)),
    refused: files.filter((file) => !isAcceptedImage(kind, file)),
  };
};
